/**
 * DataBest — Voice dictation component (Web Speech API).
 *
 * Tap-once interaction:
 *  - Click to start listening. Icon turns to a pulsing red mic.
 *  - Click again, click outside the button, or 30 seconds of silence
 *    end the dictation.
 *  - Once stopped, the final transcript is delivered via onTranscript(text).
 *
 * Language is auto-detected from navigator.language (falls back to en-US).
 *
 * NOTE: the previous MediaRecorder + faster-whisper upload flow is kept
 * below as `_legacyRecordAndUpload` for potential server-side fallback.
 * It is intentionally never invoked from the current component.
 */
import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";

const SILENCE_TIMEOUT_MS = 30000;

const getRecognitionCtor = () =>
    typeof window !== "undefined"
        ? window.SpeechRecognition || window.webkitSpeechRecognition
        : null;

const VoiceRecorder = ({ onTranscript, disabled }) => {
    const [listening, setListening] = useState(false);
    const [supported, setSupported] = useState(true);
    const recognitionRef = useRef(null);
    const silenceTimerRef = useRef(null);
    const finalTextRef = useRef("");
    const interimTextRef = useRef("");
    const buttonRef = useRef(null);
    const stopReasonRef = useRef(null); // "user" | "silence" | "outside" | null

    // Detect Web Speech API support once on mount.
    useEffect(() => {
        setSupported(!!getRecognitionCtor());
    }, []);

    const clearSilenceTimer = () => {
        if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
    };

    const armSilenceTimer = () => {
        clearSilenceTimer();
        silenceTimerRef.current = setTimeout(() => {
            stopReasonRef.current = "silence";
            stopRecognition();
        }, SILENCE_TIMEOUT_MS);
    };

    const stopRecognition = () => {
        clearSilenceTimer();
        try {
            recognitionRef.current?.stop();
        } catch (_) {
            /* ignore */
        }
        // Safety net: if onend never fires (mobile Safari / Chromium quirks),
        // force the button back to idle after 2s so the user can retry.
        setTimeout(() => {
            if (recognitionRef.current) {
                try {
                    recognitionRef.current.abort();
                } catch (_) {
                    /* ignore */
                }
            }
            setListening(false);
        }, 2000);
    };

    const start = () => {
        if (disabled || listening) return;
        const Ctor = getRecognitionCtor();
        if (!Ctor) {
            toast.error(
                "Voice dictation requires Chrome, Edge or Safari.",
            );
            return;
        }

        const recognition = new Ctor();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang =
            navigator.language ||
            (navigator.languages && navigator.languages[0]) ||
            "en-US";

        finalTextRef.current = "";
        interimTextRef.current = "";
        stopReasonRef.current = null;

        recognition.onstart = () => {
            setListening(true);
            armSilenceTimer();
        };

        recognition.onresult = (event) => {
            let interim = "";
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const res = event.results[i];
                const chunk = res[0]?.transcript ?? "";
                if (res.isFinal) {
                    finalTextRef.current =
                        (finalTextRef.current + " " + chunk).trim();
                } else {
                    interim += chunk;
                }
            }
            interimTextRef.current = interim;
            // Reset the silence timer with every new word.
            armSilenceTimer();
        };

        recognition.onerror = (event) => {
            clearSilenceTimer();
            setListening(false);
            const kind = event?.error;
            if (kind === "not-allowed" || kind === "service-not-allowed") {
                toast.error(
                    "Microphone permission denied. You can still type.",
                );
            } else if (kind === "no-speech") {
                toast.info("No speech detected — please type your comment.");
            } else if (kind !== "aborted") {
                toast.warning(`Voice error: ${kind || "unknown"}`);
            }
        };

        recognition.onend = () => {
            clearSilenceTimer();
            setListening(false);
            const finalText = [finalTextRef.current, interimTextRef.current]
                .filter(Boolean)
                .join(" ")
                .trim();
            if (finalText) onTranscript?.(finalText);
        };

        recognitionRef.current = recognition;
        try {
            recognition.start();
        } catch (err) {
            toast.error("Could not start voice dictation.");
        }
    };

    const stop = () => {
        if (!listening) return;
        stopReasonRef.current = "user";
        stopRecognition();
    };

    // Outside-click closer: any click that is NOT on this button stops dictation.
    useEffect(() => {
        if (!listening) return;
        const onDocClick = (e) => {
            if (buttonRef.current && buttonRef.current.contains(e.target)) return;
            stopReasonRef.current = "outside";
            stopRecognition();
        };
        document.addEventListener("click", onDocClick, true);
        return () => document.removeEventListener("click", onDocClick, true);
    }, [listening]);

    // Cleanup on unmount.
    useEffect(() => {
        return () => {
            clearSilenceTimer();
            try {
                recognitionRef.current?.abort();
            } catch (_) {
                /* ignore */
            }
        };
    }, []);

    const handleClick = () => {
        if (listening) stop();
        else start();
    };

    const label = !supported
        ? "Not supported"
        : listening
        ? "Listening… tap to stop"
        : "Tap to dictate";

    return (
        <div className="flex flex-col items-center">
            <button
                ref={buttonRef}
                onClick={handleClick}
                disabled={disabled || !supported}
                data-testid="voice-record-button"
                className={`relative flex h-14 w-14 items-center justify-center rounded-full transition-all ${
                    listening
                        ? "scale-110 bg-red-600 shadow-lg shadow-red-300"
                        : "bg-brand-blue-700 hover:bg-brand-blue-800"
                } text-white disabled:opacity-50`}
                aria-label={label}
                title={
                    !supported
                        ? "Voice dictation requires Chrome, Edge or Safari"
                        : label
                }
            >
                {!supported ? (
                    <MicOff className="h-6 w-6" />
                ) : (
                    <Mic
                        className={`h-6 w-6 ${
                            listening ? "animate-pulse" : ""
                        }`}
                    />
                )}
                {listening && (
                    <span className="absolute inset-0 rounded-full ring-4 ring-red-400/50 animate-ping" />
                )}
            </button>
            <span className="mt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {label}
            </span>
        </div>
    );
};

export default VoiceRecorder;

/* ============================================================
 * LEGACY (DISABLED) — MediaRecorder + faster-whisper upload.
 * Kept intact for a possible future server-side fallback.
 * Not imported or invoked anywhere in the current UI.
 * ============================================================
 *
 * async function _legacyRecordAndUpload({ onTranscript, disabled }) {
 *     if (disabled) return;
 *     const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
 *     const chunks = [];
 *     const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
 *     mr.ondataavailable = (e) => e.data?.size && chunks.push(e.data);
 *     mr.onstop = async () => {
 *         const blob = new Blob(chunks, { type: "audio/webm" });
 *         await onTranscript(blob, "audio/webm");
 *         stream.getTracks().forEach((t) => t.stop());
 *     };
 *     mr.start();
 *     // caller decides when to call mr.stop()
 *     return mr;
 * }
 */
